/**
 * The one thing that is always pressable.
 *
 * A player works out that they need teleport locomotion by starting to feel
 * ill, and at that moment the answer cannot be "take the headset off and change
 * a desktop setting". It also cannot be "open the pause menu", because on an
 * Apple Vision Pro there is no button to open one with: no thumbstick, no face
 * buttons, nothing but a pinch and wherever the eyes happen to be. Without a
 * plate like this in the room, a session on that hardware has no way into a
 * menu at all.
 *
 * So: a small dim plate parked in the bottom-left of the readout dock, next to
 * the vitals, where the game's other instruments live. Pinch it and the run
 * stops and the comfort panel opens; pinch a row and the setting changes. Two
 * presses from anywhere, which is the whole requirement.
 *
 * It is deliberately the size it is. At the dock's design distance it is about
 * 7.8° by 2.7°, and the hit test around it is more generous still — a gaze ray
 * is nothing like as precise as a mouse and there is no cursor to correct with.
 * Everything else about it is restraint: dim amber on near-black, no glow, no
 * icon, held at well under full opacity so it reads as a label on an instrument
 * rather than as a button in a game.
 */

import { CanvasPanel } from './panel';
import { AMBER_DIM, AMBER_FAINT, face, LINE_FAINT, text } from './theme';

/** Metres at the dock's design distance. See the header for the angles. */
export const TAB_WIDTH = 0.17;
export const TAB_HEIGHT = 0.06;

/** As bright as it is ever allowed to get. It is furniture, not a call to action. */
const MAX_ALPHA = 0.62;

/** Seconds to fade. Matches the readouts it sits beside. */
const FADE = 0.22;

export class ComfortTab {
  readonly panel = new CanvasPanel(TAB_WIDTH, TAB_HEIGHT);
  private alpha = 0;
  private painted = false;

  constructor() {
    this.panel.setOpacity(0);
  }

  update(dt: number, visible: boolean): void {
    if (!this.painted) {
      this.painted = true;
      this.draw();
    }
    const target = visible ? MAX_ALPHA : 0;
    this.alpha += Math.sign(target - this.alpha) * Math.min(Math.abs(target - this.alpha), dt / FADE);
    this.panel.setOpacity(this.alpha);
  }

  dispose(): void {
    this.panel.dispose();
  }

  private draw(): void {
    const p = this.panel;
    p.paint((c, w, h) => {
      face(c, w, h, LINE_FAINT);
      const mid = h / 2;
      // A hairline bracket down the left edge, the same mark the toasts use to
      // say "this is a channel, not a notice".
      c.globalAlpha = 1;
      c.fillStyle = AMBER_FAINT;
      c.fillRect(p.px(0.008), p.px(0.012), Math.max(1, p.px(0.0025)), h - p.px(0.024));
      text(c, 'COMFORT', w / 2 + p.px(0.004), mid, {
        px: p.px(0.021),
        color: AMBER_DIM,
        align: 'center',
        tracking: p.px(0.003),
      });
    });
  }
}
